import React, { useContext, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { deleteEmployeePto, getEmployeePto } from "requests/employee/employeePto";
import responseHandler from "responseHandler";
import MGrid, { MGridAction, MGridColumnDefinition } from "components/Shared/MGrid";
import { DATA_TYPE_BUTTON, DATA_TYPE_DATE } from "typeConstants/dataTypes";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import PtoAddDialogContent from "./PtoAddDialogContent";
import { PTO_OPERATIONS_DIRECTOR_ADD, PTO_OPERATIONS_MANAGER_ADD } from "typeConstants/rights";
import { userHasRight } from "selectors/rights";
import { EmployeePtoDto, EmployeeRoleEnum } from "interfaces/Mueller.Business.Models";

interface EmployeePtoProps {
  employeeId: number,
  employeeRoleId: number,
  internetUserId: number,
  //state
  signedInUserInternetUserId: number,
  canEditOpsManagerPto: boolean,
  canEditOpsDirectorPto: boolean,
  //dispatch
  makeErrorToast: (text: string) => void,
  makeSuccessToast: (text: string) => void,
}

const EmployeePto = (props: EmployeePtoProps) => {

  const [ptoData, setPtoData] = useState<EmployeePtoDto[]>(null);
  const [isFetchingPto, setIsFetchingPto] = useState(true);

  const mounted = useRef(true);

  const dialog = useContext<DialogProviderValue>(DialogContext);

  const getPto = () => {
    getEmployeePto(
      props.employeeId,
      mounted,
      (ptos) => {
        setPtoData(ptos);
        setIsFetchingPto(false);
      },
      () => {
        props.makeErrorToast("Failed to fetch PTO for employee, please try again");
      }
    );
  };

  useEffect(() => {
    // Fetch PTO data for the employee
    getPto();
  }, [props.employeeId]);

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  const onDeletePtoButtonHit = (ptoId: number) => {
    dialog.showYesNo(
      "Confirm Delete",
      "Are you sure you want to delete this PTO entry?",
      () => {
        setIsFetchingPto(true);
        dialog.hide();
        // Call the delete API
        deleteEmployeePto(
          ptoId,
          mounted,
          () => {
            props.makeSuccessToast("PTO deleted successfully!");
            // Refresh the PTO list
            getPto();
          },
          () => {
            props.makeErrorToast("Failed to delete PTO, please try again");
            setIsFetchingPto(false);
          }
        );
      }
    );
  };

  const columnDefinitions: MGridColumnDefinition[] = [
    {
      key: "dateStart",
      dataSource: "dateStart",
      name: "Start Date",
      dataType: DATA_TYPE_DATE,
      width: 80,
    },
    {
      key: "dateEnd",
      dataSource: "dateEnd",
      name: "End Date",
      dataType: DATA_TYPE_DATE,
      width: 80,
    },
    {
      key: "comment",
      dataSource: "notes",
      name: "Comment",
    },
  ];

  const canEditPto =
    (props.canEditOpsManagerPto && [EmployeeRoleEnum.FIELD_OPERATIONS_MANAGER, EmployeeRoleEnum.LENDER_LOGISTICS_MANAGER].includes(props.employeeRoleId))
    || (props.canEditOpsDirectorPto && props.employeeRoleId === EmployeeRoleEnum.FIELD_OPERATIONS_DIRECTOR)
    || (props.internetUserId === props.signedInUserInternetUserId);

  if (canEditPto) {
    columnDefinitions.push({
      key: "delete-button",
      dataSource: (row: EmployeePtoDto) => (<i className="material-icons material-icons" onClick={() => onDeletePtoButtonHit(row.id)}>delete</i>),
      name: "",
      width: 100,
      dataType: DATA_TYPE_BUTTON,
    });
  }

  const onPtoAddCompleteCallback = () => {
    setIsFetchingPto(true);
    props.makeSuccessToast("PTO added successfully!");
    getPto();
    dialog.hide();
  };

  const gridActions: MGridAction[] = [
    {
      icon: "add",
      helpText: "Add PTO",
      onClick: () => {
        dialog.show(
          "Add Employee PTO",
          <PtoAddDialogContent
            employeeId={props.employeeId}
            onAddCompleted={onPtoAddCompleteCallback}
            onCancelClick={dialog.hide}
            onAddErrorCallback={props.makeErrorToast}
          />,
          null,
          { width: 600 }
        );
      },
    },
  ];

  return(
    <div className="employee-pto-grid-container">
      <MGrid 
        columnDefinitions={columnDefinitions}
        data={ptoData}
        isFetchingData={isFetchingPto}
        actions={canEditPto ? gridActions : null}
        cardStyle
        headerText="Employee PTO"
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    signedInUserInternetUserId: state.user.profile.internetUserId,
    canEditOpsManagerPto: userHasRight(PTO_OPERATIONS_MANAGER_ADD)(state),
    canEditOpsDirectorPto: userHasRight(PTO_OPERATIONS_DIRECTOR_ADD)(state),
  };
};

const mapDispatchToProps = dispatch => ({
  makeErrorToast(text: string) {
    responseHandler.customToast(text, "error", dispatch);
  },
  makeSuccessToast(text: string) {
    responseHandler.customToast(text, "success", dispatch);
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeePto);